import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { InputTextModule } from 'primeng/inputtext';
import { choiceListElementDefinition } from '../../entity-definitions/choice-list-element.definition';
import { ChoiceListElement } from '../../types/choice-list-element.interface';
import { FieldConfig } from '../../types/entity-definition.interface';
import { CesButtonSecondaryComponent, CesDeleteButtonComponent } from '../ui/ces-buttons';
import { CesTextFieldComponent } from '../ui/ces-text-field/ces-text-field.component';

@Component({
  selector: 'app-choice-list-elements-editor',
  standalone: true,
  imports: [
    NgClass,
    FormsModule,
    InputTextModule,
    TranslocoModule,
    CesTextFieldComponent,
    CesDeleteButtonComponent,
    CesButtonSecondaryComponent,
  ],
  templateUrl: './choice-list-elements-editor.component.html',
})
export class ChoiceListElementsEditorComponent implements AfterViewInit, OnInit {
  @Input() parentId: number | undefined;
  @Input() elements: Partial<ChoiceListElement>[] = [];
  @Input() showValidation = true;
  @Input() isUsed = false;
  @Input() originalElements: ChoiceListElement[] = [];

  @Output() elementsChange = new EventEmitter<Partial<ChoiceListElement>[]>();
  @Output() validityChange = new EventEmitter<boolean>();

  @ViewChildren(NgModel) nameModels!: QueryList<NgModel>;
  @ViewChildren(CesTextFieldComponent) textFields!: QueryList<CesTextFieldComponent>;

  private duplicateNameMap = new Map<string, number[]>();

  private readonly nameErrors = new Map<string, boolean>();
  private readonly originalNames = new Map<number, string>();
  choiceListElementDefinition = choiceListElementDefinition;
  validationTrigger: number = 0;

  fieldConfig(field: string): FieldConfig | undefined {
    return this.choiceListElementDefinition.fields?.find(f => f.field === field);
  }

  ngOnInit(): void {
    if (this.elements.length === 0) {
      this.addElement();
    }

    this.originalElements.forEach(element => {
      if (element.id) {
        this.originalNames.set(element.id, element.name);
      }
    });

    this.updateDuplicateTracker();
    this.notifyValidity();
  }

  ngAfterViewInit(): void {
    this.nameModels.changes.subscribe(() => {
      this.notifyValidity();
    });
  }

  addElement(): void {
    const newElement: Partial<ChoiceListElement> = {
      name: '',
      choiceListId: this.parentId ?? 0,
      sortNr: this.calculateNextSortNr(),
    };

    this.elements.push(newElement);
    this.updateDuplicateTracker();
    this.notifyChange();
  }

  removeElement(index: number): void {
    if (this.elements.length > 1) {
      this.elements.splice(index, 1);
      this.updateDuplicateTracker();
      this.notifyChange();
    }
  }

  canDeleteElement(element: Partial<ChoiceListElement>): boolean {
    return this.isUsed ? !element.id : true;
  }

  onNameChange(newName: string, element: Partial<ChoiceListElement>, _index: number): void {
    element.name = newName;
    this.updateDuplicateTracker();
    this.notifyChange();
  }

  private updateDuplicateTracker(): void {
    this.duplicateNameMap.clear();
    this.elements.forEach((element, index) => {
      if (element.name?.trim()) {
        const normalizedName = element.name.trim().toLowerCase();
        const indices = this.duplicateNameMap.get(normalizedName) ?? [];
        indices.push(index);
        this.duplicateNameMap.set(normalizedName, indices);
      }
    });

    for (const [name, indices] of this.duplicateNameMap.entries()) {
      if (indices.length <= 1) {
        this.duplicateNameMap.delete(name);
      }
    }
  }

  isDuplicate(element: Partial<ChoiceListElement>, index: number): boolean {
    if (!element.name?.trim()) return false;

    const normalizedName = element.name.trim().toLowerCase();
    const indices = this.duplicateNameMap.get(normalizedName);

    if (indices !== undefined) {
      if (indices.includes(index) && indices.length > 1) {
        return true;
      }
    }

    return false;
  }

  hasNameError(element: Partial<ChoiceListElement>): boolean {
    return this.nameErrors.get(this.getElementKey(element)) === true;
  }

  hasAnyError(element: Partial<ChoiceListElement>, index: number): boolean {
    return (index === 0 && !element.name?.trim()) || this.isDuplicate(element, index);
  }

  isValid(): boolean {
    if (this.elements.length === 0 || !this.elements[0]?.name?.trim()) {
      return false;
    }

    if (this.duplicateNameMap.size > 0) {
      return false;
    }
    if (this.isUsed && this.originalElements.length > 0) {
      for (const originalElement of this.originalElements) {
        const stillExists = this.elements.some(element => element.id === originalElement.id);
        if (!stillExists) {
          return false;
        }
      }
    }
    return true;
  }

  private getElementKey(element: Partial<ChoiceListElement>): string {
    return `${element.id ?? 'new'}_${element.name}`;
  }

  private calculateNextSortNr(): number {
    if (this.elements.length === 0) return 10;
    return Math.max(...this.elements.map(e => e.sortNr ?? 0)) + 10;
  }

  private notifyChange(): void {
    this.elementsChange.emit([...this.elements]);
    this.notifyValidity();
  }

  private notifyValidity(): void {
    this.validityChange.emit(this.isValid());
  }
}
